import { useQueryClient } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { Icon, Sheet } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { switchBand } from "../../app/switchBand.js";
import { useActiveBand } from "../../app/activeBand.js";
import { Avatar } from "./Avatar.js";
import { initials } from "./accountRoles.js";
import styles from "./MobileShell.module.scss";
import { useAvatarUrl } from "../../app/hooks/useAvatar.js";
import { RoleLabel } from "../../features/contacts/RoleLabel.js";
import { useMyInvites } from "../../app/hooks/useMyInvites.js";
import { openPendingInvites } from "../../app/pendingInvitesPanel.js";
import { StarBandButton } from "../../features/shared/StarBandButton.js";

type SeatSummary = components["schemas"]["SeatSummary"];
type PersonSummary = components["schemas"]["PersonSummary"];

export interface AccountSheetProps {
  person: PersonSummary | undefined;
  seats: readonly SeatSummary[];
  activeBandId: string | undefined;
  onClose: () => void;
  onLogout: () => void;
  /** Navigate to an SPA route (profile edit). Provided by the shell. */
  onNavigate?: (route: string) => void;
}

/**
 * The mobile account sheet — "tap the avatar → account sheet": a profile card
 * (name · email · EDIT ›), then the **Switch band** list (every seat the PERSON holds, each with their role
 * on it and a check on the active one), then the foot (+ New band · Sign out). It is
 * the mobile-native presentation of the desktop `BandSwitcher`: picking a band runs the same audited
 * `switchBand` SCORCH so no other band's cache/DOM survives.
 *
 * Kind-reconciliation: the example shows a per-band UNREAD badge, but `SeatSummary` carries no
 * per-seat unread count (the O(1) badge is active-context only) — so the count is omitted rather than
 * fabricated (record-not-invent, the discipline). Band rows DO carry a photo — `SeatSummary.avatarMediaId`,
 * the same field the desktop `BandSwitcher` renders — resolved per row by {@link SeatAvatar}.
 */
export function AccountSheet({ person, seats, activeBandId, onClose, onLogout, onNavigate }: AccountSheetProps) {
  const queryClient = useQueryClient();
  const myInvites = useMyInvites();
  const setActiveBand = useActiveBand((s) => s.setActiveBand);

  const bandSeats = seats.filter((s) => s.kind === "band");
  const displayName = person?.displayName || person?.email || "You";
  const email = person?.email ?? "";
  // The active band seat, and whether it is a BAND IDENTITY (owner — "the artist IS the band"): its photo
  // lives on the band, not the person, so the profile card resolves the band avatar in that case (the header
  // chip does the same). `person.avatarUrl` is the CDN slot nothing fills, so a person face is resolved from
  // `avatarMediaId` through the authenticated global avatar route either way.
  const activeSeat = bandSeats.find((s) => s.bandId === activeBandId);
  const isBandIdentity = activeSeat?.isOwner === true;
  const selfAvatarUrl = useAvatarUrl(
    isBandIdentity ? "band" : "person",
    isBandIdentity ? activeSeat?.bandId : person?.personId,
    isBandIdentity ? activeSeat?.avatarMediaId : person?.avatarMediaId,
  );

  async function select(bandId: string | null | undefined) {
    onClose();
    if (!bandId || bandId === activeBandId) return;
    await switchBand(bandId, {
      queryClient,
      // The hub connection is PERSON-bound: a band switch changes only the band-scoped group
      // membership (declarative via MobileShell's useLiveGroup), so the scorch passes no realtime client — a
      // passed client would stop the whole person connection. The cache clear does the rest (mirrors desktop).
      realtime: null,
      navigate: (id) => setActiveBand(id),
    });
  }

  const pendingInvites = myInvites.data?.items?.length ?? 0;

  return (
    <Sheet title="Account" onClose={onClose} closeLabel="Close account menu">
      <button
        type="button"
        className={styles.accountProfile}
        onClick={() => {
          onClose();
          onNavigate?.("/profile");
        }}
      >
        <Avatar url={selfAvatarUrl} mark={initials(displayName)} large square={isBandIdentity} />
        <span className={styles.accountProfileText}>
          <span className={styles.accountName}>{displayName}</span>
          {email && <span className={styles.accountEmail}>{email}</span>}
        </span>
        <span className={styles.accountEdit} aria-hidden="true">
          EDIT ›
        </span>
      </button>

      {/* An org-only principal holds no band seats — show just the profile card + foot, never an orphan
          "Switch band" heading over an empty list (designed-empty-state).*/}
      {bandSeats.length > 0 && (
        <>
          <p className={styles.accountEyebrow}>Switch band</p>
          <ul className={styles.sheetList}>
            {bandSeats.map((seat) => {
              const active = seat.bandId === activeBandId;
              return (
                <li key={seat.bandId} className={styles.accountRowItem}>
                  <button
                    type="button"
                    className={styles.accountRow}
                    aria-current={active ? "true" : undefined}
                    onClick={() => void select(seat.bandId)}
                  >
                    <SeatAvatar seat={seat} />
                    <span className={styles.accountRowText}>
                      <span className={styles.accountRowName}>{seat.displayName}</span>
                      <span className={styles.accountRowSub}>
                        {/* The BAND list: these are band seats, so the role goes through RoleLabel — a
                            full-access member gets the AV mark and the owner reads "Artist" (ART-171). */}
                        <RoleLabel role={seat.role} titleKey={seat.titleKey} owner={seat.isOwner} />
                      </span>
                    </span>
                    {active && (
                      <span className={styles.accountCheck}>
                        <Icon icon={Check} size={20} />
                      </span>
                    )}
                  </button>
                  {/* ART-319: star the band the app opens on — a sibling of the row, so it never switches.
                      Only with more than one band: with one, there is nothing to choose. */}
                  {seat.bandId && bandSeats.length > 1 && (
                    <StarBandButton bandId={seat.bandId} bandName={seat.displayName ?? "this band"} />
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}

      <div className={styles.accountFoot}>
        {/* ART-239: present only when something is waiting — the same rule as the desktop dropdowns. */}
        {pendingInvites > 0 && (
          <button
            type="button"
            className={styles.accountFootLink}
            onClick={() => {
              onClose();
              openPendingInvites();
            }}
          >
            Pending invites ({pendingInvites})
          </button>
        )}
        {/* "+ New band" goes through the /apply approval funnel — a full navigation to the public page. There is no
            "+ Create organization" any more (2026-10-15): a company applies through the same form's company tab.*/}
        <button
          type="button"
          className={styles.accountFootLink}
          onClick={() => {
            onClose();
            window.location.assign("/apply");
          }}
        >
          + New band
        </button>
        <button type="button" className={styles.accountFootLink} onClick={onLogout}>
          Sign out
        </button>
      </div>
    </Sheet>
  );
}

/**
 * One band-switch row's avatar: the band's MAIN photo (Brand slot 1) when it has one, else initials — a
 * rounded square, because a band is never a circle. A component per row because the picture rides a hook
 * (`useAvatarUrl`), which cannot be called inside the row `.map`. Mirrors the desktop `BandSwitcher`.
 */
function SeatAvatar({ seat }: { seat: SeatSummary }) {
  const url = useAvatarUrl("band", seat.bandId, seat.avatarMediaId);
  return <Avatar url={url} mark={initials(seat.displayName)} square />;
}
